import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';

import { DateShift, DateSuggestionInterval } from '@mews-distributor/common';
import { subtractNumbersAsDecimals } from '@mews/framework';
import { WallClockDate, WallClockDateService } from '@mews/framework/datetime';

export interface MinLengthDateSuggestionsOptions {
    startDate: WallClockDate;
    endDate: WallClockDate;
    todayDate: WallClockDate;
    minLengthDays: number;
}

export interface DateSuggestion {
    startDate: string;
    endDate: string;
    groupLabel: DateSuggestionInterval;
    label: typeof DateShift.OtherRestrictionSuggestions;
}

interface DateSuggestionLabelOptions {
    selectedStartDate: WallClockDate;
    selectedEndDate: WallClockDate;
    suggestedStartDate: WallClockDate;
    suggestedEndDate: WallClockDate;
}

const getDateSuggestionLabel = ({
    selectedStartDate,
    suggestedStartDate,
    selectedEndDate,
    suggestedEndDate,
}: DateSuggestionLabelOptions): DateSuggestionInterval => {
    if (
        WallClockDateService.diffDays(selectedStartDate, suggestedStartDate) === 0 &&
        WallClockDateService.diffDays(suggestedEndDate, selectedEndDate) > 0
    ) {
        return DateSuggestionInterval.LaterDeparture;
    }
    if (
        WallClockDateService.diffDays(selectedEndDate, suggestedEndDate) === 0 &&
        WallClockDateService.diffDays(selectedStartDate, suggestedStartDate) > 0
    ) {
        return DateSuggestionInterval.EarlierArrival;
    }
    return DateSuggestionInterval.OtherDates;
};

export const getMinLengthDateSuggestions = ({
    startDate: selectedStartDate,
    endDate: selectedEndDate,
    minLengthDays,
    todayDate,
}: MinLengthDateSuggestionsOptions): DateSuggestion[] => {
    const dateInterval = WallClockDateService.diffDays(selectedEndDate, selectedStartDate);

    const daysAdjustment = subtractNumbersAsDecimals(minLengthDays, dateInterval);
    const dateSuggestions = [];

    for (let addDays = 0; addDays <= daysAdjustment; addDays++) {
        const removeDays = daysAdjustment - addDays;
        const suggestedStartDate = WallClockDateService.subtractDays(selectedStartDate, removeDays);
        const suggestedEndDate = WallClockDateService.addDays(selectedEndDate, addDays);

        if (WallClockDateService.diffDays(suggestedStartDate, todayDate) >= 0) {
            dateSuggestions.push({
                startDate: suggestedStartDate,
                endDate: suggestedEndDate,
                groupLabel: getDateSuggestionLabel({
                    suggestedStartDate,
                    suggestedEndDate,
                    selectedEndDate,
                    selectedStartDate,
                }),
                label: DateShift.OtherRestrictionSuggestions,
            });
        }
    }

    return pipe(
        dateSuggestions,
        array.map(({ startDate: suggestedStartDate, endDate: suggestedEndDate, groupLabel, label }) => ({
            startDate: suggestedStartDate.value,
            endDate: suggestedEndDate.value,
            groupLabel,
            label,
        }))
    );
};
